/**
 * 饼图组件
 * 遵循 Opinion 图表开发规范
 */
import React, { useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import type { TRequestPieBackType } from '@op/utils/requestUtil';
import {
  formatChartParamsDate,
  textShowElement,
  textShowImgElement,
} from '@op/components/ChartCommon/formatter';
import { DBO_TM, DBO_V } from '@op/utils/format';
import { chartAllColors, channelColorMap } from '@op/constants';
import {
  channelLegendRichInfo,
  dealwithChannelChartKey,
  channelInfo,
} from '@op/components/ChannelLabel';
import { getPercentageInfo } from '@op/utils';
import useLanguage from '@op/hooks/useLanguage';

interface IPieChartProps {
  data?: TRequestPieBackType;
  dimension: 'language' | 'channel';
  getDisplayName: (name: string) => string;
  filter: any;
}

const PieChart: React.FC<IPieChartProps> = ({
  data,
  dimension,
  getDisplayName,
  filter,
}) => {
  const { formatMessage } = useLanguage();
  const eChartsRef = useRef<any>(null);
  // 创建渠道图标 rich 配置
  const createChannelRichText = useMemo(() => {
    if (dimension !== 'channel') return {};
    return channelLegendRichInfo(16, 16);
  }, [dimension]);

  // 图例格式化（渠道需要图标）
  const legendFormatter = useMemo(() => {
    if (dimension !== 'channel') return (name: string) => getDisplayName(name);

    return (name: string) => {
      const key = dealwithChannelChartKey(name.toLowerCase());
      return `{${key}|} {name|${getDisplayName(name)}}`;
    };
  }, [dimension, getDisplayName]);

  const option = useMemo(() => {
    if (!data || data.isEmpty) return {};
    const richText = createChannelRichText;
    const pieData =
      data.series?.map((item, index) => {
        // 渠道维度使用专属颜色
        let color: string;
        if (dimension === 'channel') {
          const channelKey =
            item.name.toLowerCase() as keyof typeof channelColorMap;
          color =
            channelColorMap[channelKey] ||
            chartAllColors[index % chartAllColors.length];
        } else {
          color = chartAllColors[index % chartAllColors.length];
        }

        return {
          ...item,
          itemStyle: { color },
        };
      }) || [];

    // 计算总数
    const total = data.totalSeries || 0;
    // 排序

    return {
      color: chartAllColors,
      tooltip: {
        trigger: 'item',
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) =>
          formatChartParamsDate({
            params: [params], // 注意：饼图需要包装成数组
            // 自定义左侧标题显示：渠道显示图标，语种直接显示
            textShowFun: (marker: string, seriesName: string, element: any) => {
              const originalName = element.data.name;
              if (dimension === 'channel') {
                // 使用原始名称获取渠道信息
                const info = channelInfo(originalName.toLowerCase() as any);
                return textShowImgElement(marker, info.name, info.img);
              }
              // 语种直接显示
              return textShowElement(marker, getDisplayName(originalName));
            },
            valueFormat: (value) => DBO_V.reviews(value),
            dealwithRecords(records) {
              const list = records.map((r: any) => ({
                ...r,
                value: `${DBO_V.reviews(r.originValue)} (${DBO_V.ratio(
                  params.percent,
                )})`,
              }));

              return list;
            },
            tablesConfigFunc: (records: any) => [
              {
                showHeader: true,
                records,
                columns: [
                  { key: 'text', header: DBO_TM.timeTitle(filter) },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Mentions' }),
                  },
                ],
              },
            ],
          }),
      },
      legend: {
        data: data.legend,
        bottom: 0,
        type: 'scroll',
        formatter: legendFormatter,
        textStyle:
          dimension === 'channel' ? { rich: richText } : { fontSize: 12 },
      },
      series: [
        {
          type: 'pie',
          data: pieData,
          label: {
            show: true,
            color: '#262832',
            alignTo: 'edge',
            edgeDistance: 10,
            formatter: (params: any) => {
              const percent = getPercentageInfo(params.value, total, 2);
              const percentText = params.value
                ? `${DBO_V.reviews(params.value)}(${percent})`
                : '-';

              // 渠道需要图标前缀
              const iconPrefix =
                dimension === 'channel'
                  ? `{${dealwithChannelChartKey(params.name.toLowerCase())}|}`
                  : '';

              return `${iconPrefix}{value| ${getDisplayName(
                params.name,
              )}}: {a|${percentText}}\n{hrLine|}\n{a|}`;
            },
            rich: {
              value: {
                lineHeight: 20,
                align: 'center',
              },
              a: {
                lineHeight: 20,
                align: 'center',
              },
              hrLine: {
                borderColor: 'auto',
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              ...(dimension === 'channel' ? channelLegendRichInfo(16, 16) : {}),
            },
          },
          labelLayout: (params: any) => {
            const isLeft =
              params.labelRect.x <
              eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
            const points = params.labelLinePoints;
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10,
          },
          emphasis: {
            focus: 'self',
            label: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          blur: {
            label: { opacity: 0.2 },
            itemStyle: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
        },
      ],
    };
  }, [data, dimension, getDisplayName, createChannelRichText, legendFormatter]);

  return (
    <EChartCommon
      ref={eChartsRef}
      options={option}
      height={350}
      optionsProps={{ type: 'pie', pieLabelAlignTo: 'edge' }}
    />
  );
};

export default PieChart;
